import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';

import { unsubscribe } from '@fcom/core/utils/unsubscribe';

import { ButtonMode, ButtonTheme } from './../../buttons/button/enums';
import { IconPosition } from '../../icons';
import { ResponsiveImage } from '../../images/interfaces';
import { TagTheme } from '../../tag';
import { NotificationLayout, NotificationTheme } from '../../notifications';

const stopPropagation = (event: Event): boolean => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  return false;
};

@Component({
  selector: 'fcom-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardComponent implements OnDestroy {
  readonly TagTheme = TagTheme;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;

  @Input() hasAddedItems: boolean;

  /**
   * The title of the product.
   */
  @Input() title: string;

  /**
   * The detailed description of the product.
   */
  @Input() detailText: string;

  /**
   * Control description visibility.
   */
  @Input() showDetailText = false;

  /**
   * The image of the product.
   */
  @Input() image: ResponsiveImage;

  /**
   * The current status text to be shown for the product.
   * Should be used to to show for example how many of this product we have selected
   */
  @Input() statusText: string;

  /**
   * The purchased text to be shown for the product.
   * Should be used to to show how many of this product have previously been bought
   */
  @Input() purchasedText: string;

  /**
   * Price of the product
   */
  @Input() price: string;

  /**
   * Text to be shown for view button
   */
  @Input() viewSummaryText: string;

  /**
   * Primary button loading state for the product
   */
  @Input() loading: boolean;

  /**
   * Primary button theme
   */
  @Input() buttonTheme: ButtonTheme;

  /**
   * Primary button text
   */
  @Input() buttonText: string;

  /**
   * Primary button aria text
   */
  @Input() buttonAriaText: string;

  /**
   * If button should be disabled for the product
   */
  @Input() hideButton = false;

  /**
   * Primary button icon
   */
  @Input() buttonIcon: SvgLibraryIcon = SvgLibraryIcon.PLUS;

  /**
   * Primary button icon position
   */
  @Input() buttonIconPosition: IconPosition = IconPosition.RIGHT;

  /**
   * Notification translated text
   */
  @Input() notificationText?: string;

  /**
   * Notification translated title
   */
  @Input() notificationTitle?: string;

  /**
   * Notification icon
   */
  @Input() notificationIcon?: SvgLibraryIcon;

  /**
   * Notification theme
   */
  @Input() notificationTheme: NotificationTheme = NotificationTheme.INFO;

  @Input() focusOnBtn$: Observable<boolean>;

  @Input() lowestPrice?: string = undefined;

  @Input() originalPrice?: string = undefined;

  @Input() productTagText?: string = undefined;

  @Input() productTagIcon?: SvgLibraryIcon = undefined;

  @Input() productTagTheme?: TagTheme = undefined;

  @Input() useNewProductCardLayout = false;

  @Input() countText: string = undefined;

  @Input() isLaptopUp: boolean;

  @Input() isSpecialUpsellProduct: boolean;

  @Input() reverseLayout = false;

  /**
   * Category icons to be shown in the product card. Only 1 or 2 icons are supported.
   * Should be used to show category specific icons, e.g. seat and wifi icons for wifi products
   * If one icon is provided, it will be shown as a single icon.
   * If two icons are provided, they will be joined by a plus icon.
   */
  @Input() categoryIcons?: [SvgLibraryIcon] | [SvgLibraryIcon, SvgLibraryIcon];

  /**
   * Event emitter for selectProduct event from primary button
   */
  @Output() selectProduct: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Event emitter for viewDetails event from view button
   */
  @Output() viewDetails: EventEmitter<void> = new EventEmitter<void>();

  private subscriptions: Subscription = new Subscription();

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  emitProductCategory(event: Event): void {
    stopPropagation(event);
    this.selectProduct.emit();
  }

  emitViewDetails(event: Event): void {
    stopPropagation(event);
    this.viewDetails.emit();
  }
}
